import { useFieldArray, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { IconPlus, IconSearch, IconUploadCloud01 } from "@pentabd/icons";
import {
  Button,
  InputText,
  SectionHeader,
  TableData,
  TableRow,
  TableSecondary,
  Text,
} from "@pentabd/ui";

import { SelectController } from "@components";

import { useVendorList } from "@hooks/procurements/vendors/useVendors";
import { useUser } from "@hooks/useUser";

import { getConvertedMetaData } from "@helpers/inventory/metaDataConvert";
import { getAccess } from "@helpers/permissionController";

import { FORM_FIELDS } from "@constants/form";
import { STATUS } from "@constants/status.procurement";

const TENDER_OPENING = FORM_FIELDS.PROCUREMENT_DETAILS.TENDER_OPENING;
const { APPROVED } = STATUS;

const COLUMNS = (t: any) => [
  {
    id: 1,
    name: t("PROCUREMENT_CREATE.TENDER_OPENING.FORM.TABLE.COLUMN.NAME"),
    key: "name",
  },
  {
    id: 2,
    name: t(
      "PROCUREMENT_CREATE.TENDER_OPENING.FORM.TABLE.COLUMN.PROPOSED_PRICE",
    ),
    key: "price",
  },
  {
    id: 3,
    name: t(
      "PROCUREMENT_CREATE.TENDER_OPENING.FORM.TABLE.COLUMN.TENDER_SECURITY_MONEY",
    ),
    key: "money",
  },
  {
    id: 4,
    name: t("PROCUREMENT_CREATE.TENDER_OPENING.FORM.TABLE.COLUMN.KEY_DOC"),
    key: "document",
  },
];

export default function VendorList({
  canUpload,
  setOpenModal,
  setVendorInfoId,
  vendors,
}: {
  canUpload: number | undefined;
  setOpenModal: any;
  setVendorInfoId: any;
  vendors: any;
}) {
  const { t } = useTranslation();
  const { register, setValue, control } = useFormContext();
  const { vendorState } = useVendorList(true, APPROVED);

  const { user } = useUser();
  const { realm_roles: roles } = user || {};

  const { append, fields, remove } = useFieldArray({
    control,
    name: "openingVendors",
    keyName: "key",
  });

  const handleFileUploadModal = (id: any) => {
    setOpenModal(true);
    setVendorInfoId(id);
  };

  return (
    <div className="pt-10">
      <SectionHeader
        title={t("PROCUREMENT_CREATE.TENDER_OPENING.FORM.VENDOR_LIST")}
      />
      <div className="pt-10">
        <TableSecondary columns={COLUMNS(t)}>
          {fields?.map((field: any, index: number) => {
            setValue(`openingVendors.${index}.vendorDocuments`, []);

            return (
              <TableRow key={field.key}>
                <TableData>
                  <SelectController
                    options={getConvertedMetaData(
                      vendorState?.getData?.data || [],
                    )}
                    name={`openingVendors.${index}.${TENDER_OPENING.VENDOR_ID}`}
                    control={control}
                    setValue={setValue}
                    register={{
                      ...register(
                        `openingVendors.${index}.${TENDER_OPENING.VENDOR_ID}`,
                      ),
                    }}
                    portal
                    isSearchable
                    prefix={<IconSearch size="20" fill="dark" />}
                    placeholder={t(
                      "PROCUREMENT_CREATE.TENDER_OPENING.FORM.TABLE.FORM.VENDOR.PLACEHOLDER",
                    )}
                  />
                </TableData>

                <TableData>
                  <InputText
                    {...register(
                      `openingVendors.${index}.${TENDER_OPENING.PROPOSED_PRICE}`,
                    )}
                    type="number"
                    placeholder={t(
                      "PROCUREMENT_CREATE.TENDER_OPENING.FORM.TABLE.FORM.PROPOSED_PRICE.PLACEHOLDER",
                    )}
                  />
                </TableData>

                <TableData>
                  <InputText
                    {...register(
                      `openingVendors.${index}.${TENDER_OPENING.SECURITY_MONEY}`,
                    )}
                    type="number"
                    placeholder={t(
                      "PROCUREMENT_CREATE.TENDER_OPENING.FORM.TABLE.FORM.TENDER_SECURITY_MONEY.PLACEHOLDER",
                    )}
                  />
                </TableData>

                <TableData>
                  {canUpload ? (
                    <Button
                      fill="fill"
                      type="primary"
                      size="sm"
                      onClick={() => handleFileUploadModal(field?.id)}
                    >
                      <IconUploadCloud01 size="16" fill="white" />
                      {t(
                        "PROCUREMENT_CREATE.TENDER_OPENING.FORM.TABLE.FORM.UPLOAD_BUTTON",
                      )}
                    </Button>
                  ) : (
                    <Text color="subtitle1">
                      {t(
                        "PROCUREMENT_CREATE.TENDER_OPENING.FORM.TABLE.FORM.UPLOAD_TEXT",
                      )}
                    </Text>
                  )}
                </TableData>
              </TableRow>
            );
          })}

          {getAccess(roles, "PROCUREMENT_HR") && (
            <TableRow>
              <TableData>
                <Button
                  type="light"
                  fill="outline"
                  size="sm"
                  onClick={() =>
                    append({
                      // [TENDER_OPENING.VENDOR_ID]: "",
                      // [TENDER_OPENING.PROPOSED_PRICE]: "",
                      // [TENDER_OPENING.SECURITY_MONEY]: "",
                      // [TENDER_OPENING.KEY_DOCUMENT]: [],
                    })
                  }
                >
                  <IconPlus size="20" />{" "}
                  {t(
                    "PROCUREMENT_CREATE.TENDER_OPENING.FORM.BUTTONS.ADD_ANOTHER",
                  )}
                </Button>
              </TableData>
            </TableRow>
          )}
        </TableSecondary>
      </div>
    </div>
  );
}
